import { useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Badge, Button, Group, MultiSelect, Select, Stack, Table, Text,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconAlertTriangle, IconClockPause, IconDownload, IconFileText, IconFilterOff, IconRefresh, IconX,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PageHeader } from '../components/PageHeader';
import { TablePagination } from '../components/TablePagination';
import { TableToolbar } from '../components/TableToolbar';
import { TableSkeleton } from '../components/TableSkeleton';
import { EmptyState } from '../components/EmptyState';
import { FetchOverlay } from '../components/FetchOverlay';
import { ServerSearchSelect, type PagedResp } from '../components/ServerSearchSelect';
import { SectionCard } from '../components/SectionCard';
import {
  AnsweredAt, ContractDetailModal, ContractRowActions, ContractStatusBadge, SignedViaLabel,
  CONTRACT_STATUSES, PENDING_STATUSES, STALE_DAYS, lastSentAt, waitingDays, type ContractRow,
} from '../components/StudentContracts';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { SILENT_ERROR } from '../lib/queryMeta';
import { useClasses } from '../hooks/useLookups';
import { formatDate } from '../lib/format';
import { exportCsv } from '../lib/csv';
import api from '../services/api';

/** Sana filtri serverga TOSHKENT kuni sifatida ("YYYY-MM-DD") ketadi (backend startOfDay/endOfDay). */
const wallDate = (d: Date | null) => (d ? dayjs(d).format('YYYY-MM-DD') : undefined);

/** Eksport so'rovidagi sahifa hajmi — server chegarasi bilan bir xil (`lib/paginate.ts` maxLimit = 100). */
const EXPORT_PAGE_SIZE = 100;

/**
 * Eksportning yuqori chegarasi. Cheksiz sikl brauzerni ham, serverni ham qotirib
 * qo'yishi mumkin; chegaraga yetilsa foydalanuvchi bu haqda OCHIQ ogohlantiriladi
 * (jimgina qirqib tashlamaymiz).
 */
const EXPORT_MAX_ROWS = 5000;

/**
 * SHARTNOMALAR — umumiy ro'yxat (barcha o'quvchilar bo'yicha).
 *
 * Asosiy savol: "kim javob bermayapti va qachondan beri?" — shuning uchun "javob
 * kutilmoqda" tezkor filtri (SENT+VIEWED, eng eskisi tepada) va kutish kunlari ustuni bor.
 * Holat ko'rsatish `components/StudentContracts.tsx` dagi YAGONA `ContractStatusBadge` orqali.
 */
export default function Contracts() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [expired, setExpired] = useState<string>('');
  const [classId, setClassId] = useState<string>('');
  const [studentId, setStudentId] = useState<string | null>(null);
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [sort, setSort] = useState('createdAt:desc');
  /** CSV eksporti bir nechta so'rov yuboradi — tugma shu vaqtda "yuklanmoqda" holatida turadi. */
  const [exporting, setExporting] = useState(false);

  // CHUQUR HAVOLA: bildirishnoma bosilganda `HeaderBell` shu sahifaga
  // `/contracts?id=<contractId>` bilan keladi — AYNAN o'sha shartnoma kartochkasi
  // ochilishi kerak (aks holda xodim ro'yxatdan uni qo'lda qidirishga majbur).
  const [params, setParams] = useSearchParams();
  const urlId = params.get('id');
  const [openId, setOpenId] = useState<string | null>(urlId);

  // Sahifa ochiq turganda YANGI bildirishnoma bosilsa manzil o'zgaradi, komponent esa
  // qayta yaratilmaydi — shuning uchun URL o'zgarishini RENDER bosqichida kuzatamiz
  // (useEffect bo'lsa modal bir kadr kechikib ochilardi).
  const [prevUrlId, setPrevUrlId] = useState(urlId);
  if (urlId !== prevUrlId) {
    setPrevUrlId(urlId);
    if (urlId) setOpenId(urlId);
  }

  /** Yopilganda `?id=` ham olib tashlanadi — "orqaga" tugmasi modalni qayta ochmasin. */
  const closeDetails = () => {
    setOpenId(null);
    if (params.has('id')) {
      const next = new URLSearchParams(params);
      next.delete('id');
      setParams(next, { replace: true });
    }
  };

  const { data: classes } = useClasses();

  /** Joriy filtr — jadval so'rovi ham, CSV eksporti ham AYNAN shu to'plamdan foydalanadi. */
  const filters = {
    // Qidiruv serverda kamida 2 belgidan ishlaydi — qisqasini yubormaymiz
    q: q.trim().length >= 2 ? q.trim() : undefined,
    status: statuses.length ? statuses.join(',') : undefined,
    expired: expired || undefined,
    classId: classId || undefined,
    studentId: studentId || undefined,
    from: wallDate(from),
    to: wallDate(to),
    sort,
  };

  // `SILENT_ERROR`: xato ro'yxat o'rnida ochiq ko'rsatiladi (Alert + qayta urinish),
  // shu sabab global qizil toast shu xabarni ikkinchi marta takrorlamaydi.
  const {
    items: rows, isLoading, isError, fetching, total, pages, page, setPage, refetch, limit, setLimit} = usePagedQuery<ContractRow>('contracts', '/contracts', filters, 20, true, SILENT_ERROR);

  /** "Javob kutilmoqda" rejimi: SENT+VIEWED va eng uzoq kutayotgani tepada. */
  const pendingMode =
    sort === 'createdAt:asc' &&
    statuses.length === PENDING_STATUSES.length &&
    PENDING_STATUSES.every((s) => statuses.includes(s));

  const togglePending = () => {
    if (pendingMode) {
      setStatuses([]);
      setSort('createdAt:desc');
    } else {
      setStatuses([...PENDING_STATUSES]);
      setSort('createdAt:asc');
    }
    setPage(1);
  };

  const statusData = CONTRACT_STATUSES.map((s) => ({ value: s, label: t(`contractList.status${s}`) }));

  /** Barcha filtrlarni bir bosishda tozalash (saralash ham standart holatiga qaytadi). */
  const clearAll = () => {
    setSearch('');
    setStatuses([]);
    setExpired('');
    setClassId('');
    setStudentId(null);
    setFrom(null);
    setTo(null);
    setSort('createdAt:desc');
    setPage(1);
  };

  /**
   * T12 — FAOL FILTRLAR CHIPLARI.
   * Toolbar tanlagichlari ko'p va ekranga sig'may ketadi: xodim "Javob kutilmoqda"
   * yoqiqligini sezmay "imzolanganlar ko'rinmayapti" deb o'ylagan. Endi har bir faol
   * filtr alohida chip bo'lib turadi va "x" bilan BITTA bosishda olib tashlanadi.
   *
   * O'quvchi ismi qatorlardan olinadi: `studentId` filtri serverda qo'llangani uchun
   * ro'yxatdagi barcha qator AYNAN o'sha o'quvchiniki (qo'shimcha so'rov shart emas).
   */
  const studentName = rows.find((c) => c.student?.id === studentId)?.student?.fullName;
  const activeChips: { key: string; label: string; onClear: () => void }[] = [];
  if (search.trim()) {
    activeChips.push({ key: 'q', label: t('contractsPage.searchChip', { q: search.trim() }), onClear: () => setSearch('') });
  }
  if (pendingMode) {
    // Sort + statuslar birgalikda yoqilgan rejim — chip ham ularni BIRGA o'chiradi.
    activeChips.push({ key: 'pending', label: t('contractsPage.pendingQuick'), onClear: togglePending });
  } else {
    statuses.forEach((s) => activeChips.push({
      key: `status-${s}`,
      label: `${t('fields.status')}: ${t(`contractList.status${s}`)}`,
      onClear: () => setStatuses(statuses.filter((x) => x !== s)),
    }));
  }
  if (expired) {
    activeChips.push({
      key: 'expired',
      label: expired === '1' ? t('contractsPage.expiredOnly') : t('contractsPage.notExpired'),
      onClear: () => setExpired(''),
    });
  }
  if (classId) {
    activeChips.push({
      key: 'class',
      label: `${t('fields.class')}: ${(classes ?? []).find((c) => c.id === classId)?.name ?? classId}`,
      onClear: () => setClassId(''),
    });
  }
  if (studentId) {
    activeChips.push({
      key: 'student',
      label: studentName ? `${t('contractList.student')}: ${studentName}` : t('contractsPage.studentFilter'),
      onClear: () => setStudentId(null),
    });
  }
  if (from) activeChips.push({ key: 'from', label: `${t('common.dateFrom')}: ${formatDate(from)}`, onClear: () => setFrom(null) });
  if (to) activeChips.push({ key: 'to', label: `${t('common.dateTo')}: ${formatDate(to)}`, onClear: () => setTo(null) });

  /** Ro'yxat qisqarishiga SABAB bo'ladigan filtr bormi — bo'sh holat matni shunga qarab o'zgaradi. */
  const hasFilters = activeChips.length > 0;

  /**
   * Joriy filtr bo'yicha BARCHA qatorlarni yig'adi.
   * Jadval bir vaqtda faqat bitta sahifani ko'rsatadi — eksport esa avval JIMGINA shu
   * sahifanigina yozardi (20 ta qator) va xodim buni bilmasdi. Endi sahifalar ketma-ket
   * so'raladi; `EXPORT_MAX_ROWS` ga yetilsa natija qirqiladi va bu haqda ogohlantiriladi.
   */
  const fetchAllRows = async (): Promise<ContractRow[]> => {
    const all: ContractRow[] = [];
    for (let p = 1; ; p++) {
      const { data } = await api.get<PagedResp<ContractRow>>('/contracts', {
        params: { page: p, limit: EXPORT_PAGE_SIZE, ...filters },
      });
      all.push(...data.items);
      if (!data.items.length || p >= data.pages || all.length >= EXPORT_MAX_ROWS) break;
    }
    return all.slice(0, EXPORT_MAX_ROWS);
  };

  const exportAsCsv = (list: ContractRow[]) =>
    exportCsv(
      `shartnomalar-${dayjs().format('YYYY-MM-DD')}`,
      list.map((c) => ({
        number: c.contractNumber ?? c.data?.contractNumber ?? '',
        student: c.student?.fullName ?? c.data?.studentFullName ?? '',
        class: c.student?.class?.name ?? '',
        status: t(`contractList.status${c.status}`),
        expired: c.isExpired ? t('contractList.expired') : '',
        created: formatDate(c.createdAt),
        // "Kutish (kun)" AYNAN shu sanadan hisoblanadi — qayta yuborilgan shartnomada u
        // `created` dan farq qiladi, shuning uchun ustun alohida chiqariladi.
        sent: formatDate(lastSentAt(c)),
        answered: c.signedAt ?? c.rejectedAt ?? c.cancelledAt ? formatDate(c.signedAt ?? c.rejectedAt ?? c.cancelledAt) : '',
        waiting: PENDING_STATUSES.includes(c.status) ? waitingDays(c) : '',
      })),
      {
        number: t('contractList.number'),
        student: t('contractList.student'),
        class: t('fields.class'),
        status: t('fields.status'),
        expired: t('contractList.expired'),
        created: t('contractList.created'),
        sent: t('contractsPage.colLastSent'),
        answered: t('contractList.answeredAt'),
        waiting: t('contractsPage.colWaiting'),
      }
    );

  const exportRows = async () => {
    setExporting(true);
    try {
      const all = await fetchAllRows();
      if (!all.length) return;
      exportAsCsv(all);
      if (all.length < total) {
        notifications.show({
          color: 'yellow',
          message: t('contractsPage.exportTruncated', { n: all.length, total }),
        });
      }
    } catch {
      notifications.show({ color: 'red', message: t('contractsPage.exportFailed') });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t('pages.contracts.title')}
        subtitle={`${t('common.total')}: ${total}`}
        action={
          // Yorliq va maslahat OCHIQ aytadi: eksport JORIY SAHIFANI emas, filtrga mos
          // BARCHA shartnomalarni yozadi (ular sahifama-sahifa yig'iladi).
          <Tooltip label={t('contractsPage.exportAllHint')} withArrow multiline w={260}>
            <Button
              variant="light"
              leftSection={<IconDownload size={18} />}
              disabled={!rows.length}
              loading={exporting}
              onClick={() => void exportRows()}
            >
              {t('contractsPage.exportCsv')}
            </Button>
          </Tooltip>
        }
      />

      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder={t('contractsPage.search')}
        sort={sort}
        onSort={(v) => { setSort(v); setPage(1); }}
        sortOptions={[
          { value: 'createdAt:desc', label: t('contractsPage.sortNewest') },
          { value: 'createdAt:asc', label: t('contractsPage.sortLongestWaiting') },
          { value: 'contractNumber:desc', label: t('contractsPage.sortNumberDesc') },
          { value: 'contractNumber:asc', label: t('contractsPage.sortNumberAsc') },
          { value: 'signedAt:desc', label: t('contractsPage.sortSigned') },
          { value: 'expiresAt:asc', label: t('contractsPage.sortExpiringSoon') },
          { value: 'status:asc', label: t('contractsPage.sortStatus') },
        ]}
      >
        <Button
          variant={pendingMode ? 'filled' : 'light'}
          color={pendingMode ? 'orange' : 'gray'}
          leftSection={<IconClockPause size={16} />}
          onClick={togglePending}
        >
          {t('contractsPage.pendingQuick')}
        </Button>
        <MultiSelect
          placeholder={t('fields.status')}
          data={statusData}
          value={statuses}
          onChange={(v) => { setStatuses(v); setPage(1); }}
          clearable
          w={210}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          placeholder={t('contractsPage.filterExpiry')}
          data={[
            { value: '1', label: t('contractsPage.expiredOnly') },
            { value: '0', label: t('contractsPage.notExpired') },
          ]}
          value={expired || null}
          onChange={(v) => { setExpired(v ?? ''); setPage(1); }}
          clearable
          w={175}
        />
        <Select
          placeholder={t('fields.class')}
          data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
          value={classId || null}
          onChange={(v) => { setClassId(v ?? ''); setPage(1); }}
          searchable clearable w={130}
        />
        <ServerSearchSelect
          placeholder={t('contractsPage.studentFilter')}
          endpoint="/students"
          mapOption={(st: { id: string; fullName: string; class?: { name: string } | null }) => ({
            value: st.id,
            label: `${st.fullName}${st.class ? ` · ${st.class.name}` : ''}`,
          })}
          value={studentId}
          onChange={(v) => { setStudentId(v); setPage(1); }}
          w={200}
        />
        <DateInput
          placeholder={t('common.dateFrom')}
          valueFormat="DD.MM.YYYY"
          value={from}
          onChange={(v) => { setFrom(v); setPage(1); }}
          clearable w={150}
        />
        <DateInput
          placeholder={t('common.dateTo')}
          valueFormat="DD.MM.YYYY"
          value={to}
          onChange={(v) => { setTo(v); setPage(1); }}
          clearable w={150}
        />
        <TableExport<ContractRow>
          endpoint="/contracts"
          params={filters}
          filename={t('nav.contracts')}
          total={total}
          ustunlar={[
            { key: 'number', label: t('contractList.number') },
            { key: 'studentName', label: t('contractList.student') },
            { key: 'status', label: t('fields.status') },
            { key: 'createdAt', label: t('fields.date') },
          ]}
          sanaMaydoni={(r) => r.createdAt}
        />
      </TableToolbar>

      {/* Faol filtrlar — ko'rinadigan chiplar + "hammasini tozalash" (T12) */}
      {hasFilters && (
        <Group gap="xs" mb="md" wrap="wrap">
          <Text size="sm" c="dimmed">{t('contractsPage.activeFilters')}:</Text>
          {/* Chiplar ekranga sig'may o'ralib ketsa ham, nechta filtr yoqiqligi bitta son bo'lib ko'rinadi. */}
          <Badge size="sm" circle variant="filled" color="orange">{activeChips.length}</Badge>
          {activeChips.map((chip) => (
            <Badge
              key={chip.key}
              size="lg"
              variant="light"
              color="brand"
              style={{ textTransform: 'none' }}
              rightSection={
                <ActionIcon
                  size="xs"
                  variant="transparent"
                  color="brand"
                  aria-label={`${t('contractsPage.removeFilter')}: ${chip.label}`}
                  onClick={chip.onClear}
                >
                  <IconX size={13} />
                </ActionIcon>
              }
            >
              {chip.label}
            </Badge>
          ))}
          <Button size="compact-sm" variant="subtle" color="gray" leftSection={<IconFilterOff size={15} />} onClick={clearAll}>
            {t('contractsPage.clearAll')}
          </Button>
        </Group>
      )}

      <SectionCard title={t('common.list')} icon={IconFileText} p={0}>
        {isLoading ? (
          <TableSkeleton rows={6} cols={6} />
        ) : isError ? (
          // XATO holat ochiq ko'rsatiladi — bo'sh ro'yxat bilan aralashib ketmasin
          <Stack align="center" gap="sm" p="lg">
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('common.error')} w="100%">
              {t('contractList.loadError')}
            </Alert>
            <Button variant="light" loading={fetching} leftSection={<IconRefresh size={16} />} onClick={() => void refetch()}>
              {t('contractList.retry')}
            </Button>
          </Stack>
        ) : !rows.length ? (
          /*
           * BO'SH NATIJANING IKKI SABABI BOR va ular xodim uchun bir xil ko'rinardi:
           * (a) haqiqatan shartnoma yo'q, (b) FILTR uni yashirmoqda. Egasi "imzolangan
           * shartnomalar yo'qolgan" deb shikoyat qilgan holat aynan (b) edi — "Javob
           * kutilmoqda" yoqiq turgan, u esa SIGNED ni ataylab chiqaradi. Chiplar jadval
           * tepasida bo'lsa ham e'tibordan chetda qolgan, shuning uchun sabab AYNAN bo'sh
           * holatning ichida aytiladi va tozalash tugmasi shu yerning o'zida turadi.
           */
          <EmptyState
            icon={IconFileText}
            title={hasFilters ? t('contractsPage.emptyFiltered') : t('pages.contracts.empty')}
            description={
              // Qaysi filtr yashirayotgani NOMMA-NOM sanaladi — "filtrni o'zgartiring"
              // degan umumiy maslahat qaysi biri aybdorligini aytmaydi.
              hasFilters
                ? t('contractsPage.emptyFilteredDesc', { list: activeChips.map((c) => c.label).join(', ') })
                : t('pages.contracts.emptyDesc')
            }
            action={hasFilters ? (
              <Button variant="light" leftSection={<IconFilterOff size={16} />} onClick={clearAll}>
                {t('contractsPage.clearAll')}
              </Button>
            ) : undefined}
          />
        ) : (
          <Table.ScrollContainer minWidth={980} pos="relative">
            <FetchOverlay visible={fetching} />
            <Table highlightOnHover verticalSpacing="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('contractList.number')}</Table.Th>
                  <Table.Th>{t('contractList.student')}</Table.Th>
                  <Table.Th>{t('fields.status')}</Table.Th>
                  <Table.Th>{t('contractList.created')}</Table.Th>
                  <Table.Th>{t('contractList.answeredAt')}</Table.Th>
                  <Table.Th>{t('contractList.signedVia')}</Table.Th>
                  <Table.Th w={190} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((c) => {
                  // Kutish hisobi OXIRGI yuborilgan paytdan yuriladi: qayta yuborilgan
                  // shartnoma "yangi" havola bilan boshqatdan kutadi (`waitingDays`).
                  const stale = PENDING_STATUSES.includes(c.status) && waitingDays(c) >= STALE_DAYS;
                  return (
                    <Table.Tr key={c.id}>
                      <Table.Td fw={500}>{c.contractNumber ?? c.data?.contractNumber ?? '—'}</Table.Td>
                      <Table.Td>
                        <Stack gap={2}>
                          {c.student ? (
                            <Anchor component={Link} to={`/students/${c.student.id}`} size="sm" fw={500} lineClamp={1}>
                              {c.student.fullName}
                            </Anchor>
                          ) : (
                            <Text size="sm" lineClamp={1}>{c.data?.studentFullName ?? '—'}</Text>
                          )}
                          {c.student?.class && <Badge size="xs" variant="default">{c.student.class.name}</Badge>}
                        </Stack>
                      </Table.Td>
                      <Table.Td><ContractStatusBadge status={c.status} isExpired={c.isExpired} /></Table.Td>
                      <Table.Td>{formatDate(c.createdAt)}</Table.Td>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <AnsweredAt contract={c} />
                          {stale && (
                            // Nishon qaysi sanadan hisoblanayotganini OCHIQ aytadi —
                            // qayta yuborilgan shartnomada bu `createdAt` dan farq qiladi.
                            <Tooltip label={t('contractsPage.lastSentAt', { at: formatDate(lastSentAt(c)) })} withArrow>
                              <Badge size="xs" color="orange" variant="light">{t('contractsPage.stale', { n: STALE_DAYS })}</Badge>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td><SignedViaLabel via={c.signedVia} /></Table.Td>
                      <Table.Td>
                        <ContractRowActions contract={c} onDetails={() => setOpenId(c.id)} />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>

      <TablePagination
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />

      <ContractDetailModal id={openId} opened={!!openId} onClose={closeDetails} />
    </>
  );
}
